// Opening a saved route, track or point of interest: load it (GET /routes/{guid}, /tracks/{guid}; a point of
// interest on its own is a route of routeType 2) and work out what Plan shows - the line(s), points of interest,
// distance, climb and an elevation profile.
import { getDraft, isDraft } from './drafts';
import { apiFetch } from '@/api/client';
import type { PoiDetails } from './poi';
import { POINT_OF_INTEREST, poiDetailsOf, readPoi, routePois } from './poi';

export type TrailKind = 'route' | 'track' | 'poi';

/** The API's path for a kind: a point of interest on its own is stored as a route. */
export const apiPathOf = (kind: TrailKind) => (kind === 'track' ? 'tracks' : 'routes');

export interface ApiPoint {
  guid?: string;
  latitude: number;
  longitude: number;
  elevation?: number;
  pointType?: number;
  /** 1 = a visible waypoint; other values are points that only shape the line. */
  usage?: number;
  code?: string;
  warningRadius?: number;
  timestamp?: string;
  name?: string;
  description?: string;
  deleted?: boolean;
  /** A point of interest's website and phone: only kept in the xgoPois extension's records (poi.ts). */
  website?: string;
  phone?: string;
}

/** A route's extension (an attribute); the xgoPois one holds its points of interest (poi.ts). */
export interface ApiExtension {
  description?: string;
  attributeType?: { code?: string; tag?: string; description?: string; dataType?: number };
  valueString?: string;
  [key: string]: unknown;
}

export interface ApiTrail {
  guid: string;
  name?: string;
  description?: string;
  created?: string;
  activityTypeDesc?: string;
  /** The activity type (GET /activityTypes); 0 or missing when none. */
  activityTypeId?: number;
  /** 1 a route, 2 a point of interest on its own. */
  routeType?: number;
  /** A standalone point of interest's website and phone, as JSON (poi.ts). */
  detail?: string;
  points?: ApiPoint[];
  segments?: { points?: ApiPoint[] }[];
  extensions?: ApiExtension[];
}

/** A position as [lon, lat]. */
export type LonLat = [number, number];

export interface PointOfInterest extends PoiDetails {
  position: LonLat;
}

/** A waypoint marker: green start, orange "smartie" via points, chequered finish. */
export interface Waypoint {
  position: LonLat;
  kind: 'start' | 'via' | 'finish';
  /** Degrees clockwise from north towards the next waypoint; undefined at the finish. */
  bearing?: number;
}

export interface Trail {
  kind: TrailKind;
  guid: string;
  name: string;
  description: string;
  activity: string;
  /** The activity type's id; 0 when none (or only a name, from an imported file). */
  activityTypeId: number;
  created?: string;
  /** One line per track segment (routes have one). */
  lines: LonLat[][];
  pois: PointOfInterest[];
  /** A point of interest on its own: its place and details (also its only entry in pois). */
  poi?: PointOfInterest;
  /** Start, via waypoints and finish (routes); start and finish (tracks). */
  waypoints: Waypoint[];
  distanceKm: number;
  ascentM: number;
  descentM: number;
  /** Recorded time for tracks, in seconds. */
  durationS?: number;
  /** Distance along the trail (km) against elevation (m), when the points have elevations. */
  profile: ProfilePoint[];
  /** Each line point's height in metres, null where unknown (filled in by elevation.ts). */
  heights: (number | null)[][];
}

/**
 * A point of interest beside the route rather than part of its line: a PointOfInterest point carrying POI
 * data. Plain pointType 2 points inside a route's line are control points (waypoints), as XpeditionGO and
 * the legacy apps treat them.
 */
export const isPoi = (p: ApiPoint) =>
  p.pointType === POINT_OF_INTEREST && ((p.code?.startsWith('xgo:') ?? false) || (p.warningRadius ?? 0) > 0);

/** A point Xpedition2 shows as a waypoint marker (as XpeditionGO's routeWaypoints). */
export const isWaypoint = (p: ApiPoint) => !isPoi(p) && ((p.pointType ?? 0) > 1 || p.usage === 1);
/** Climbs smaller than this are GPS noise. */
const CLIMB_THRESHOLD_M = 3;

/** A route's or track's JSON: its unsaved changes if it has any, otherwise as saved. */
export async function loadTrailJson(kind: TrailKind, guid: string): Promise<ApiTrail> {
  const draft = getDraft(guid);
  if (draft) return draft.json;
  // A new, unsaved trail is held in memory (gone after a reload, so it can't be opened).
  if (isDraft(guid)) throw new Error('Unsaved trail no longer available');
  const response = await apiFetch(`/${apiPathOf(kind)}/${guid}`);
  if (!response.ok) throw new Error(`GET ${kind}: ${response.status}`);
  return (await response.json()) as ApiTrail;
}

/** Query options for one route or track (used with useQueries for several at once). */
export function trailQuery(kind: TrailKind, guid: string) {
  return {
    queryKey: ['trail', kind, guid] as const,
    staleTime: 5 * 60_000,
    queryFn: async () => toTrail(kind, await loadTrailJson(kind, guid)),
  };
}

export function toTrail(kind: TrailKind, data: ApiTrail): Trail {
  const valid = (p: ApiPoint) =>
    !p.deleted &&
    Math.abs(p.latitude) <= 90 &&
    Math.abs(p.longitude) <= 180 &&
    !(p.latitude === 0 && p.longitude === 0);

  let pointLines: ApiPoint[][];
  let pois: PointOfInterest[] = [];
  let poi: PointOfInterest | undefined;
  if (kind === 'poi') {
    // On its own: just the point (legacy ones may lack XpeditionGO's code).
    const found = readPoi({ ...data, points: (data.points ?? []).filter(valid) });
    pointLines = [];
    if (found) {
      poi = { ...found.details, position: found.position };
      pois = [poi];
    }
  } else if (kind === 'route') {
    const points = (data.points ?? []).filter(valid);
    pointLines = [points.filter((p) => !isPoi(p))];
    pois = routePois({ ...data, points }).map((p) => ({
      ...poiDetailsOf(p),
      position: [p.longitude, p.latitude] as LonLat,
    }));
  } else {
    pointLines = (data.segments ?? []).map((s) => (s.points ?? []).filter(valid));
  }
  pointLines = pointLines.filter((line) => line.length > 0);

  const lines = pointLines.map((line) => line.map((p) => [p.longitude, p.latitude] as LonLat));
  let heights = pointLines.map((line) =>
    line.map((p) => (typeof p.elevation === 'number' ? p.elevation : null)),
  );
  // Heights that are all 0 mean the file had none (some planners write 0).
  if (heights.every((line) => line.every((h) => h === null || h === 0)))
    heights = heights.map((l) => l.map(() => null));
  const { distanceKm, ascentM, descentM, profile } = climb(lines, heights);

  let durationS: number | undefined;
  if (kind === 'track') {
    const times = pointLines
      .flat()
      .map((p) => (p.timestamp ? Date.parse(p.timestamp) : NaN))
      .filter((t) => Number.isFinite(t) && t > Date.UTC(1990, 0, 1));
    // A loop, not Math.max(...times): a long recording has more points than a call can take arguments.
    let first = Infinity;
    let last = -Infinity;
    for (const t of times) {
      if (t < first) first = t;
      if (t > last) last = t;
    }
    if (times.length > 1) durationS = (last - first) / 1000;
  }

  return {
    kind,
    guid: data.guid,
    name: data.name ?? '',
    description: data.description ?? '',
    activity: data.activityTypeDesc ?? '',
    activityTypeId: data.activityTypeId ?? 0,
    created: data.created,
    lines,
    pois,
    poi,
    waypoints: pickWaypoints(pointLines, kind),
    distanceKm,
    ascentM,
    descentM,
    durationS,
    profile,
    heights,
  };
}

/**
 * Distance, climb and the elevation profile of lines whose points have these heights (null = unknown, left out
 * of the profile and climb).
 */
/** A point of the elevation profile: where it is along the trail and on the map, and the climb up to it. */
export interface ProfilePoint {
  km: number;
  m: number;
  at: LonLat;
  /** Ascent and descent from the start to here, in metres. */
  up: number;
  down: number;
}

export function climb(lines: LonLat[][], heights: (number | null)[][]) {
  let distanceKm = 0;
  let ascentM = 0;
  let descentM = 0;
  const profile: Trail['profile'] = [];
  lines.forEach((line, l) => {
    let reference: number | undefined;
    line.forEach((point, i) => {
      const previous = line[i - 1];
      if (previous) distanceKm += kmBetween(previous, point);
      const m = heights[l]?.[i];
      if (m === null || m === undefined) return;
      if (reference === undefined) reference = m;
      else if (m - reference >= CLIMB_THRESHOLD_M) {
        ascentM += m - reference;
        reference = m;
      } else if (reference - m >= CLIMB_THRESHOLD_M) {
        descentM += reference - m;
        reference = m;
      }
      profile.push({ km: distanceKm, m, at: point, up: Math.round(ascentM), down: Math.round(descentM) });
    });
  });
  return { distanceKm, ascentM: Math.round(ascentM), descentM: Math.round(descentM), profile };
}

/**
 * The start, the finish and (for routes) the points marked as waypoints, each with the bearing to the
 * next. Repeats are skipped so a waypoint never points at itself, but a loop keeps its finish.
 */
function pickWaypoints(lines: ApiPoint[][], kind: TrailKind): Waypoint[] {
  const points = lines.flat();
  const picked: LonLat[] = [];
  points.forEach((point, i) => {
    const last = i === points.length - 1;
    if (i !== 0 && !last && !(kind === 'route' && isWaypoint(point))) return;
    const position: LonLat = [point.longitude, point.latitude];
    const previous = picked.at(-1);
    const repeat = previous && previous[0] === position[0] && previous[1] === position[1];
    if (!repeat || (last && picked.length === 1)) picked.push(position);
    else if (last) picked[picked.length - 1] = position;
  });
  if (picked.length < 2) return [];
  return picked.map((position, i) => {
    const next = picked[i + 1];
    return {
      position,
      kind: i === 0 ? 'start' : next ? 'via' : 'finish',
      bearing: next ? bearingDeg(position, next) : undefined,
    };
  });
}

/** Initial great-circle bearing from a to b, degrees clockwise from north. */
export function bearingDeg([lon1, lat1]: LonLat, [lon2, lat2]: LonLat): number {
  const rad = Math.PI / 180;
  const y = Math.sin((lon2 - lon1) * rad) * Math.cos(lat2 * rad);
  const x =
    Math.cos(lat1 * rad) * Math.sin(lat2 * rad) -
    Math.sin(lat1 * rad) * Math.cos(lat2 * rad) * Math.cos((lon2 - lon1) * rad);
  return (Math.atan2(y, x) / rad + 360) % 360;
}

/** Great-circle distance in km. */
export function kmBetween([lon1, lat1]: LonLat, [lon2, lat2]: LonLat): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371.0088 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** "4 h 25 min", "35 min". */
export function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}
